import {ModalType} from "../../../Framework/Types/ModalTypes";
import Modal from "../../../Framework/Components/Modals/Modal";
import {FormWrapperLabel} from "../../../Framework/Components/Forms/FormEntryWrapper/FormEntryWrapper";
import {DateRangePicker} from "react-date-range";
import React, {useEffect, useState} from "react";
import {ButtonSubmit} from "../../../Framework/Components/Buttons/Button";
import {ColourMod} from "../../../Framework/Data/Enums/LayoutEnums";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {PaginatedResponseType} from "../../../Types/Response/PaginatedResponseType";
import {useInfiniteUsers, UserDataType} from "../../../Api/UsersEndpoints";
import {
    SendHistoryFilterType,
    SendsPageDataType,
    useInfiniteSendsPage,
    useInfiniteShortListSendsPage
} from "../../../Api/StandaloneSendsEndpoints";
import {formatIsoDate} from "../../../Framework/Constants/DateHelpers";
import ComboBoxControl from "../../../Framework/Components/Forms/ComboBox/ComboBoxControl";
import { InfiniteData } from '@tanstack/query-core/src/types';
import { GetItemCounts } from '../../../Framework/Constants/DataHelpers';
import InfiniteDataComboBox from '../../InfiniteDataComboBox';

interface FilterSendsHistoryModalType extends ModalType {
    filterData?: SendHistoryFilterType;
    hidePageDetails?: boolean;
    hideUserDetails?: boolean;
}

const FilterSendsUsers = (props: any) => {
    const { isLoading: usersLoading, data: usersData, error: usersError, dataUpdatedAt: usersDataUpdatedAt, fetchNextPage: fetchNextUsersPage, hasNextPage: hasNextUsersPage, isFetchingNextPage: isFetchingNextUsersPage }  = useInfiniteUsers();

    const [ usersItemCounts, setUsersItemCounts] = useState<any>({ loaded: 0, total: 0});
    const [ UserOptions, setUserOptions] = useState<any>([]);

    const MapUserOptions = (data: InfiniteData<PaginatedResponseType>) => {
        setUsersItemCounts(GetItemCounts(data));
        const newUsers = data?.pages.map((page: any) => page?.data && page.data.map((user: UserDataType, index: number) => {
            return { 'label': user.name, 'value': user.userId }
        }));

        setUserOptions(newUsers.flat());

        if(hasNextUsersPage) {
            fetchNextUsersPage().then(r => null);
        }
    }

    useEffect(() => {
        if(usersData) {
            MapUserOptions(usersData);
        }
    }, [usersData, usersDataUpdatedAt]);

    return (
        <InfiniteDataComboBox
            name={'userIds'}
            label={'Users'}
            placeholder={'Select Users...'}
            data={UserOptions}
            defaultValues={props.filterData?.userIds}
            isLoading={usersLoading}
            valueType={'string'}
            isFetchingNextPage={isFetchingNextUsersPage}
            fetchNextPage={fetchNextUsersPage}
            hasNextPage={hasNextUsersPage}
            itemCounts={usersItemCounts}
        />
    )
}

const FilterSendsPages = (props: any) => {
    const {
        data: pagesData,
        error: pagesError,
        isLoading: pagesLoading,
        dataUpdatedAt: pagesDataUpdatedAt, fetchNextPage: fetchNextPagesPage, hasNextPage: hasNextPagesPage, isFetchingNextPage: isFetchingNextPagesPage
    } = useInfiniteSendsPage();

    const [ pagesItemCounts, setPagesItemCounts] = useState<any>({ loaded: 0, total: 0});
    const [ PagesOptions, setPagesOptions] = useState<any>([]);

    const MapPagesOptions = (data: InfiniteData<PaginatedResponseType>) => {
        setPagesItemCounts(GetItemCounts(data));
        const newPages = data?.pages.map((page: any) => page?.data && page.data.map((page: SendsPageDataType, index: number) => {
            return { 'label': page.title, 'value': page.id }
        } ));

        setPagesOptions(newPages.flat());

        if(hasNextPagesPage) {
            fetchNextPagesPage().then(r => null);
        }
    }

    useEffect(() => {
        if(pagesData) {
            MapPagesOptions(pagesData);
        }
    }, [pagesData, pagesDataUpdatedAt]);

    return (
        <InfiniteDataComboBox
            name={'pageIds'}
            label={'Profiles'}
            placeholder={'Select Profiles...'}
            data={PagesOptions}
            defaultValues={props.defaultValues}
            isLoading={pagesLoading}
            valueType={'number'}
            isFetchingNextPage={isFetchingNextPagesPage}
            fetchNextPage={fetchNextPagesPage}
            hasNextPage={hasNextPagesPage}
            itemCounts={pagesItemCounts}
        />
    )
}

const FilterSendsPagesUser = (props: any) => {
    const {
        data: pagesData,
        error: pagesError,
        isLoading: pagesLoading,
        dataUpdatedAt: pagesDataUpdatedAt, fetchNextPage: fetchNextPagesPage, hasNextPage: hasNextPagesPage, isFetchingNextPage: isFetchingNextPagesPage
    } = useInfiniteShortListSendsPage();

    const [ pagesItemCounts, setPagesItemCounts] = useState<any>({ loaded: 0, total: 0});
    const [ PagesOptions, setPagesOptions] = useState<any>([]);

    const MapPagesOptions = (data: InfiniteData<PaginatedResponseType>) => {
        setPagesItemCounts(GetItemCounts(data));
        const newPages = data?.pages.map((page: any) => page?.data && page.data.map((page: SendsPageDataType, index: number) => {
            return { 'label': page.title, 'value': page.id }
        } ));

        setPagesOptions(newPages.flat());

        if(hasNextPagesPage) {
            fetchNextPagesPage().then(r => null);
        }
    }

    useEffect(() => {
        if(pagesData) {
            MapPagesOptions(pagesData);
        }
    }, [pagesData, pagesDataUpdatedAt]);

    return (
        <InfiniteDataComboBox
            name={'pageIds'}
            label={'Profiles'}
            placeholder={'Select Profiles...'}
            data={PagesOptions}
            defaultValues={props.defaultValues}
            isLoading={pagesLoading}
            valueType={'number'}
            isFetchingNextPage={isFetchingNextPagesPage}
            fetchNextPage={fetchNextPagesPage}
            hasNextPage={hasNextPagesPage}
            itemCounts={pagesItemCounts}
        />
    )
}

const FilterSendsHistoryModal = (props: FilterSendsHistoryModalType) => {
    const formMethods = useForm();
    const { control, handleSubmit, formState, reset } = formMethods;

    const [dateRanges, setDateRanges] = useState<any>([
        {
            startDate: props.filterData?.startDate ? new Date(props.filterData?.startDate) : null,
            endDate: props.filterData?.endDate ? new Date(props.filterData?.endDate) : null,
            key: "selection"
        }
    ]);

    const HandleFilterChange = (data: any) => {
        let newData: SendHistoryFilterType = { }

        if(data.pageIds?.length > 0) {
            newData.pageIds = data.pageIds;
        }

        if(data.userIds?.length > 0) {
            newData.userIds = data.userIds;
        }

        if(data.dateRange) {
            newData.startDate = formatIsoDate(data.dateRange?.startDate);
            newData.endDate = formatIsoDate(data.dateRange?.endDate);
        }

        if(props.actionedCallback) {
            props.actionedCallback(newData);
        }
    }

    useEffect(() => {
        reset();
    }, [props.visible])

    return (
        <Modal title={'Filter History'} isOpen={props.visible} closeCallback={props.closeCallback}>

            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit((data: any) => HandleFilterChange(data))}>
                    <fieldset className={'flex-column flex-gap-md'}>

                        {!props.hidePageDetails && !props.hideUserDetails && (
                            <FilterSendsPages defaultValues={props.filterData?.pageIds} />
                        )}

                        {!props.hidePageDetails && props.hideUserDetails && (
                            <FilterSendsPagesUser defaultValues={props.filterData?.pageIds} />
                        )}

                        {!props.hideUserDetails && (
                            <FilterSendsUsers {...props} control={control} />
                        )}

                        <FormWrapperLabel label={'Date Range'}>
                            <Controller
                                control={control}
                                name='dateRange'
                                render={({ field, fieldState }) =>
                                    <DateRangePicker
                                        {...field}
                                        showDateDisplay={false}
                                        maxDate={new Date()}
                                        editableDateInputs={true}
                                        onChange={(item: any) => {
                                            setDateRanges([item.selection]);
                                            field.onChange(item.selection);
                                        }}
                                        moveRangeOnFirstSelection={false}
                                        ranges={dateRanges}
                                        inputRanges={[]}
                                    />
                                } />
                        </FormWrapperLabel>

                        <ButtonSubmit fullWidth={true} colour={ColourMod.Primary} title={'Filter'} disabled={!formState.isValid} />

                    </fieldset>
                </form>
            </FormProvider>

        </Modal>
    )
}

export default FilterSendsHistoryModal
